import React from "react";
import { Helmet } from "react-helmet-async";

const StructuredData = ({ data }) => {
  const jsonLd = JSON.stringify(data);
  return (
    <Helmet>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: jsonLd }}
      />
    </Helmet>
  );
};

// Local Business Schema
export const LocalBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "HomeGoodsStore",
  "@id": "https://chandelierluxe.co.za#business",
  name: "Chandelier Luxe",
  image: "https://chandelierluxe.co.za/assets/images/hero-chandelier.jpeg",
  url: "https://chandelierluxe.co.za",
  telephone: "+27844229825",
  email: "info@chandelierluxe.co.za",
  priceRange: "$$",
  address: {
    "@type": "PostalAddress",
    streetAddress: "34 Dion Close, Strathavon",
    addressLocality: "Sandton",
    addressRegion: "Gauteng",
    postalCode: "2031",
    addressCountry: "ZA",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: -26.2041,
    longitude: 28.0473,
  },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "09:00",
      closes: "17:00",
    },
  ],
  areaServed: {
    "@type": "Country",
    name: "South Africa",
  },
  sameAs: [],
};

// Service Schema
export const ServiceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Chandelier Services",
  provider: {
    "@type": "LocalBusiness",
    "@id": "https://chandelierluxe.co.za#business",
    name: "Chandelier Luxe",
  },
  areaServed: {
    "@type": "Country",
    name: "South Africa",
  },
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Chandelier Services",
    itemListElement: [
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Chandelier Installation",
          description:
            "Professional installation of crystal chandeliers with expert craftsmanship",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Chandelier Restoration",
          description:
            "Restoration of vintage and antique chandeliers to their original glory",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Chandelier Maintenance",
          description:
            "Regular cleaning and upkeep services for crystal chandeliers",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Custom Chandelier Design",
          description:
            "Bespoke chandelier solutions for unique spaces and requirements",
        },
      },
    ],
  },
};

// Organization Schema
export const OrganizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": "https://chandelierluxe.co.za#organization",
  name: "Chandelier Luxe",
  url: "https://chandelierluxe.co.za",
  logo: "https://chandelierluxe.co.za/assets/images/hero-chandelier.jpeg",
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+27844229825",
    contactType: "Customer Service",
    email: "info@chandelierluxe.co.za",
    areaServed: "ZA",
    availableLanguage: ["en"],
  },
  address: {
    "@type": "PostalAddress",
    streetAddress: "34 Dion Close, Strathavon",
    addressLocality: "Sandton",
    addressRegion: "Gauteng",
    postalCode: "2031",
    addressCountry: "ZA",
  },
};

// Product Schema for individual products
export const ProductSchema = (product) => {
  // Handle image URL - if it's a module import (string), use it as is for structured data
  // In production, webpack will handle the image path
  let imageUrl = product.image;
  if (typeof product.image === "string") {
    imageUrl = product.image.startsWith("http")
      ? product.image
      : `https://chandelierluxe.co.za${product.image}`;
  } else {
    // If it's a webpack module, we can't determine the URL easily in structured data
    // Use a placeholder or the hero image
    imageUrl =
      "https://chandelierluxe.co.za/assets/images/hero-chandelier.jpeg";
  }

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: `${product.name} - ${
      product.dimensions || ""
    } Luxury crystal chandelier in ${
      product.category || "Classic"
    } style. Available for professional installation in Johannesburg, South Africa.`,
    image: imageUrl,
    sku: `CH-${product.id}`,
    category: product.category || "Chandeliers",
    brand: {
      "@type": "Brand",
      name: "Chandelier Luxe",
    },
    offers: {
      "@type": "Offer",
      url: `https://chandelierluxe.co.za/product/${product.id}`,
      priceCurrency: "ZAR",
      availability: "https://schema.org/InStock",
      seller: {
        "@type": "Organization",
        name: "Chandelier Luxe",
      },
    },
  };
};

// Breadcrumb Schema
export const BreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
});

export default StructuredData;
